import { z } from 'zod';
/* ==========================================================================
 *  Custom food wire contract (T11.4 / ISSUE-074)
 *
 *  Custom foods are stored in user_food_override (no new table — it already
 *  has the right schema and GDPR cascade). The sourceId convention is:
 *    barcode override: sourceId = "<barcode>"  (numeric, 8–14 digits)
 *    custom food:      sourceId = "cf_<uuid>"  (cf_ prefix, never numeric)
 *
 *  Macros are always stored per_100g in the DB. When the user enters macros
 *  per_serving, the route handler normalises and stores serving info in
 *  householdServings: [{ label: '1 serving', grams: servingG }].
 * ========================================================================== */
/** The 5 core macros accepted for a custom food. */
export const customFoodMacroInputSchema = z.object({
    kcal: z.number().nonnegative(),
    proteinG: z.number().nonnegative(),
    carbsG: z.number().nonnegative(),
    fatG: z.number().nonnegative(),
    fiberG: z.number().nonnegative(),
});
/** A custom food as returned by the API. Macros are always per_100g. */
export const customFoodSchema = z.object({
    id: z.string(),
    name: z.string(),
    brand: z.string().nullable(),
    /** Normalised macros per 100g. */
    macrosPer100g: customFoodMacroInputSchema,
    /** Gram weight of one serving when the user entered per-serving macros. Null otherwise. */
    servingG: z.number().positive().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** POST /me/custom-foods */
export const createCustomFoodSchema = z
    .object({
    name: z.string().min(1).max(200),
    brand: z.string().max(200).optional(),
    /**
     * 'per_100g': macros are for 100g (stored as-is).
     * 'per_serving': macros are for one serving; servingG required.
     *   Server normalises to per_100g: stored_macro = entered_macro * 100 / servingG.
     */
    basis: z.enum(['per_100g', 'per_serving']).default('per_100g'),
    /** Gram weight of one serving — required when basis='per_serving'. */
    servingG: z.number().positive().optional(),
    macros: customFoodMacroInputSchema,
})
    .refine((v) => v.basis !== 'per_serving' || !!v.servingG, {
    message: 'servingG is required when basis is per_serving.',
    path: ['servingG'],
});
/** PATCH /me/custom-foods/:id */
export const updateCustomFoodSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    brand: z.string().max(200).nullable().optional(),
    basis: z.enum(['per_100g', 'per_serving']).optional(),
    servingG: z.number().positive().nullable().optional(),
    macros: customFoodMacroInputSchema.optional(),
});
export const customFoodsListSchema = z.array(customFoodSchema);
//# sourceMappingURL=custom-food.js.map